import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PageMeta } from "@/components/common/PageMeta";
import { SignalMotif } from "@/components/common/SignalMotif";
import { HeroMotion } from "@/components/home/HeroMotion";
import { DisciplineConstellation } from "@/components/home/DisciplineConstellation";
import { PROOF_STRIP, HOW_STAGES, CAPABILITY_DOMAINS, PORTFOLIO_GATEWAYS, HOME_PORTFOLIO_INTRO } from "@/lib/content";
import { ResponsiveImage } from "@/components/common/ResponsiveImage";

const PROCESS = ["Fragmented question", "Structured research hypothesis", "Technical architecture", "Tested prototype", "Transferable asset"];

export default function Home() {
  return (
    <>
      <PageMeta title={null} />

      {/* Hero */}
      <section className="bg-[var(--paper)]">
        <div className="container-dbrf grid items-stretch gap-0 lg:grid-cols-[44%_56%]">
          <div className="fade-up py-14 pr-0 lg:py-24 lg:pr-12">
            <p className="eyebrow mb-5">Scientific and engineering operating platform</p>
            <h1 className="font-display font-extrabold leading-[1.0] text-navy" style={{ fontSize: "clamp(2.6rem,5vw,4.6rem)" }}>
              Rare expertise.<br />Assembled around<br /><span className="text-[var(--cobalt)]">difficult problems.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[var(--mineral)]" style={{ fontSize: "clamp(1.05rem,1.4vw,1.25rem)", lineHeight: 1.6 }}>
              DBRF brings scientists, engineers and technology builders together around challenges
              that do not fit inside one discipline, turning complex questions into research
              programs, prototypes, products and intellectual property.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/bring-a-challenge" data-testid="hero-challenge-btn" className="btn-transition inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[var(--cobalt)]">
                Bring us a challenge <ArrowRight size={16} />
              </Link>
              <Link to="/capabilities" data-testid="hero-capabilities-btn" className="btn-transition inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-6 py-3.5 text-sm font-semibold text-navy hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]">
                Explore capabilities
              </Link>
            </div>
            <Link to="/join-the-network" data-testid="hero-join-link" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--cobalt)] link-underline">
              Join the professional network <ArrowUpRight size={15} />
            </Link>
          </div>
          <div className="relative min-h-[360px] lg:min-h-full">
            <div className="absolute inset-0">
              <HeroMotion />
            </div>
          </div>
        </div>
        {/* proof strip */}
        <div className="border-y border-[var(--line)] bg-[var(--ivory)]">
          <div className="container-dbrf flex flex-wrap items-center gap-x-10 gap-y-3 py-4">
            {PROOF_STRIP.map((p) => (
              <span key={p} className="font-mono text-xs uppercase tracking-[0.14em] text-[var(--mineral)]" data-testid="proof-item">
                <span className="mr-2 text-[var(--amber)]">◆</span>{p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-disciplines */}
      <section className="container-dbrf py-20 lg:py-28">
        <div className="reading">
          <p className="eyebrow mb-4">One problem, many disciplines</p>
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(2rem,3.5vw,3.4rem)", lineHeight: 1.08 }}>
            The hardest problems rarely belong to one field.
          </h2>
          <p className="mt-5 text-lg text-[var(--mineral)]">
            A system may involve perception, signal structure, machine learning, hardware
            constraints, human behaviour and commercial reality at the same time. DBRF forms the
            team around the problem — not the other way around.
          </p>
        </div>
        <div className="mt-14">
          <DisciplineConstellation />
        </div>
      </section>

      {/* How DBRF works */}
      <section className="border-y border-[var(--line)] bg-[var(--paper)] py-20 lg:py-28">
        <div className="container-dbrf">
          <p className="eyebrow mb-4">How DBRF works</p>
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.8rem,3vw,2.8rem)" }}>
            From an unclear signal to engineered technology.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
            {HOW_STAGES.map((s, idx) => (
              <div key={s.n} className="card-lift rounded-2xl border border-[var(--line)] bg-[var(--ivory)] p-6" data-testid={`how-stage-${idx}`}>
                <span className="font-mono text-sm text-[var(--cobalt)]">{s.n}</span>
                <h3 className="mt-2 font-display text-xl font-bold text-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-[var(--mineral)]">{s.desc}</p>
                <p className="mt-4 border-t border-[var(--line)] pt-3 font-mono text-xs text-[var(--mineral)]">{s.output}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-[var(--mineral)]">Not every engagement necessarily reaches commercialisation.</p>
        </div>
      </section>

      {/* Capability domains */}
      <section className="container-dbrf py-20 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow mb-4">Capability domains</p>
            <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.8rem,3vw,2.8rem)" }}>
              Capabilities that connect across the problem.
            </h2>
          </div>
          <Link to="/capabilities" data-testid="domains-explore-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--cobalt)] link-underline">
            Open the full catalogue <ArrowRight size={16} />
          </Link>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {CAPABILITY_DOMAINS.map((c) => (
            <Link
              key={c.title}
              to={`/capabilities?domain=${encodeURIComponent(c.filter)}`}
              data-testid="capability-domain-card"
              className="card-lift flex flex-col rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-7"
            >
              <h3 className="font-display text-lg font-bold text-navy">{c.title}</h3>
              <p className="mt-3 flex-1 text-sm text-[var(--mineral)]">{c.scope}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {c.tags.map((t) => (
                  <span key={t} className="rounded-full border border-[var(--line)] bg-[var(--ivory)] px-2.5 py-1 font-mono text-[0.68rem] text-[var(--mineral)]">{t}</span>
                ))}
              </div>
              <p className="mt-4 border-t border-[var(--line)] pt-3 font-mono text-xs text-[var(--mineral)]">Outputs: {c.outputs}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* From question to result */}
      <section className="border-t border-[var(--line)] bg-[var(--ivory)] py-20 lg:py-28">
        <div className="container-dbrf">
          <p className="eyebrow mb-4">From question to engineered result</p>
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.8rem,3vw,2.8rem)" }}>
            A project may stop at any appropriate stage.
          </h2>
          <SignalMotif className="mt-10" height={90} />
          <div className="mt-6 grid gap-3 md:grid-cols-5">
            {PROCESS.map((p, idx) => (
              <div key={p} className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4 text-center">
                <span className="font-mono text-xs text-[var(--cobalt)]">{String(idx + 1).padStart(2, "0")}</span>
                <p className="mt-1 text-sm font-medium text-navy">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OEM portfolio gateways */}
      <section className="container-dbrf py-20 lg:py-28">
        <p className="eyebrow mb-4">OEM portfolio</p>
        <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.8rem,3vw,2.8rem)" }}>
          From foundational methods to engineered systems.
        </h2>
        <p className="mt-4 max-w-2xl text-[var(--mineral)]">{HOME_PORTFOLIO_INTRO}</p>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PORTFOLIO_GATEWAYS.map((g) => (
            <Link
              key={g.title}
              to={`/projects?technology_family=${encodeURIComponent(g.filter)}`}
              data-testid="portfolio-gateway-card"
              className="card-lift flex flex-col rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-7"
            >
              <h3 className="font-display text-lg font-bold text-navy">{g.title}</h3>
              <p className="mt-3 flex-1 text-sm text-[var(--mineral)]">{g.blurb}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--cobalt)]">Explore <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/projects" data-testid="explore-oem-projects" className="btn-transition inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[var(--cobalt)]">
            Explore OEM projects <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Heritage teaser */}
      <section className="border-y border-[var(--line)] bg-[var(--navy)] py-20 text-[var(--ivory)] lg:py-24" data-nav-dark>
        <div className="container-dbrf grid items-center gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <p className="eyebrow mb-4 !text-[var(--cyan)]">Heritage, carried forward</p>
            <p className="font-display text-2xl leading-snug lg:text-3xl">
              DBRF’s intellectual lineage reaches back to pioneering work in quantum electronics and
              molecular approaches to computation. That heritage is not a museum piece. It is a
              standard: fundamental science should create new ways to build.
            </p>
            <Link to="/heritage" data-testid="heritage-link" className="mt-6 inline-flex items-center gap-2 font-semibold text-[var(--cyan)]">
              Explore the scientific heritage <ArrowRight size={16} />
            </Link>
          </div>
          <ResponsiveImage base="/assets/06-heritage-hero" alt="Concept art evoking a historical physics archive." sizes="(max-width: 1023px) 100vw, 40vw" className="w-full rounded-2xl border border-white/10 object-cover" />
        </div>
      </section>

      {/* Closing conversion */}
      <section className="container-dbrf py-20 lg:py-28">
        <div className="rounded-3xl border border-[var(--line)] bg-[var(--paper)] p-10 lg:p-16">
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.9rem,3.2vw,3rem)", lineHeight: 1.08 }}>
            Bring the problem that does not fit inside a standard brief.
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-[var(--mineral)]">
            Tell us what is blocked, what has already been tried and what a meaningful result would
            change. DBRF will determine whether the problem is a fit and which capabilities it may
            require.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/bring-a-challenge" data-testid="closing-challenge-btn" className="btn-transition inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[var(--cobalt)]">
              Start a challenge brief <ArrowRight size={16} />
            </Link>
            <Link to="/join-the-network" data-testid="closing-join-btn" className="btn-transition rounded-full border border-[var(--line)] px-6 py-3.5 text-sm font-semibold text-navy hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]">
              Join the network
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
